import type { QueryClient } from '@tanstack/react-query';
import { api, type Project } from '@/api/client';
import type { UndoRedoAvailability, UndoRedoResult } from './types';
import { invalidateProjectHistory } from './projectChangeHistoryQuery';
import { setProjectEntity } from '@/lib/projectEntityCache';
import { markProjectUpdatedByMutation } from '@/lib/projectRealtimeDedup';
import {
  invalidateArchivedProjectCaches,
  invalidateReadyMonthProjectLists,
  mergeSingleProjectInCaches,
  syncArchivedCachesAfterProjectUpdated,
  syncReadyMonthCachesAfterProjectUpdated,
} from '@/lib/projectListQueryCache';
import { invalidateSummariesForProjectIds } from '@/lib/summariesBatchReload';
import { isProjectExcludedFromActiveTable } from '@/utils/projectStatuses';

const STORAGE_KEY = 'tracker:lastUndoProjectId';

export interface UndoRedoUiSnapshot {
  projectId: string | null;
  canUndo: boolean;
  canRedo: boolean;
  undoPreview: string | null;
  redoPreview: string | null;
  isUndoing: boolean;
  isRedoing: boolean;
}

const EMPTY_AVAILABILITY: UndoRedoAvailability = {
  can_undo: false,
  can_redo: false,
  undo_preview: null,
  redo_preview: null,
};

function readStoredProjectId(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(STORAGE_KEY);
}

function writeStoredProjectId(projectId: string | null): void {
  if (typeof window === 'undefined') return;
  if (projectId) {
    sessionStorage.setItem(STORAGE_KEY, projectId);
  } else {
    sessionStorage.removeItem(STORAGE_KEY);
  }
}

function freezeSnapshot(snapshot: UndoRedoUiSnapshot): UndoRedoUiSnapshot {
  return Object.freeze(snapshot);
}

function snapshotFrom(
  projectId: string | null,
  availability: UndoRedoAvailability,
  pending: Pick<UndoRedoUiSnapshot, 'isUndoing' | 'isRedoing'>
): UndoRedoUiSnapshot {
  return freezeSnapshot({
    projectId,
    canUndo: availability.can_undo,
    canRedo: availability.can_redo,
    undoPreview: availability.undo_preview,
    redoPreview: availability.redo_preview,
    isUndoing: pending.isUndoing,
    isRedoing: pending.isRedoing,
  });
}

function snapshotsEqual(a: UndoRedoUiSnapshot, b: UndoRedoUiSnapshot): boolean {
  return (
    a.projectId === b.projectId &&
    a.canUndo === b.canUndo &&
    a.canRedo === b.canRedo &&
    a.undoPreview === b.undoPreview &&
    a.redoPreview === b.redoPreview &&
    a.isUndoing === b.isUndoing &&
    a.isRedoing === b.isRedoing
  );
}

let availabilityState: UndoRedoAvailability = EMPTY_AVAILABILITY;
let snapshot = snapshotFrom(readStoredProjectId(), availabilityState, {
  isUndoing: false,
  isRedoing: false,
});
const listeners = new Set<() => void>();

function emitIfChanged(next: UndoRedoUiSnapshot): void {
  if (snapshotsEqual(snapshot, next)) return;
  snapshot = next;
  for (const listener of listeners) listener();
}

function updateSnapshot(
  patch: Partial<UndoRedoUiSnapshot>,
  nextAvailability = availabilityState
): void {
  availabilityState = nextAvailability;
  emitIfChanged(
    freezeSnapshot({
      ...snapshot,
      ...patch,
      canUndo: nextAvailability.can_undo,
      canRedo: nextAvailability.can_redo,
      undoPreview: nextAvailability.undo_preview,
      redoPreview: nextAvailability.redo_preview,
    })
  );
}

export function getLastUndoRedoProjectId(): string | null {
  return snapshot.projectId ?? readStoredProjectId();
}

export function setLastUndoRedoProjectId(projectId: string | null): void {
  writeStoredProjectId(projectId);
  if (snapshot.projectId === projectId) return;
  updateSnapshot({ projectId }, EMPTY_AVAILABILITY);
}

export function subscribeUndoRedoStore(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getUndoRedoSnapshot(): UndoRedoUiSnapshot {
  return snapshot;
}

export async function refreshUndoRedoAvailability(
  queryClient: QueryClient,
  projectId: string | null | undefined
): Promise<void> {
  void queryClient;
  if (!projectId) {
    setLastUndoRedoProjectId(null);
    return;
  }
  const availability = await api.projects.getChangesAvailability(projectId);
  if (snapshot.projectId !== projectId) {
    writeStoredProjectId(projectId);
  }
  updateSnapshot({ projectId }, availability);
}

function applyProjectPayload(queryClient: QueryClient, payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const project = payload as Project;
  if (!project.id) return null;
  setProjectEntity(queryClient, project);
  mergeSingleProjectInCaches(queryClient, project);
  markProjectUpdatedByMutation(project.id);
  invalidateProjectHistory(queryClient, project.id);
  invalidateSummariesForProjectIds(queryClient, [project.id]);
  if (isProjectExcludedFromActiveTable(project.state) || project.is_archived) {
    const sync = syncReadyMonthCachesAfterProjectUpdated(queryClient, project);
    if (sync.invalidateSummaries) {
      void queryClient.invalidateQueries({ queryKey: ['ready-month-summaries'] });
    }
    invalidateReadyMonthProjectLists(queryClient, sync.readyMonthLists);
    const archSync = syncArchivedCachesAfterProjectUpdated(queryClient, project);
    invalidateArchivedProjectCaches(queryClient, {
      list: archSync.archivedList,
      count: archSync.invalidateArchivedCount,
    });
  }
  return project.id;
}

function setPending(kind: 'undo' | 'redo', value: boolean): void {
  updateSnapshot(kind === 'undo' ? { isUndoing: value } : { isRedoing: value });
}

export async function undoLastProjectChange(
  queryClient: QueryClient,
  projectId?: string | null
): Promise<UndoRedoResult> {
  const targetProjectId = projectId ?? getLastUndoRedoProjectId();
  setPending('undo', true);
  try {
    const result = targetProjectId
      ? await api.projects.undo(targetProjectId)
      : await api.projects.undoGlobal();
    if (result.ok) {
      const payloadProjectId = applyProjectPayload(queryClient, result.project);
      const resultProjectId = result.project_id ?? payloadProjectId;
      if (resultProjectId) {
        setLastUndoRedoProjectId(resultProjectId);
        await refreshUndoRedoAvailability(queryClient, resultProjectId);
      }
    }
    return result;
  } finally {
    setPending('undo', false);
  }
}

export async function redoLastProjectChange(
  queryClient: QueryClient,
  projectId: string | null | undefined
): Promise<UndoRedoResult> {
  if (!projectId) {
    return { ok: false, error_code: 'nothing_to_redo', message: 'Нет изменений для повтора' };
  }
  setPending('redo', true);
  try {
    const result = await api.projects.redo(projectId);
    if (result.ok) {
      const payloadProjectId = applyProjectPayload(queryClient, result.project);
      const resultProjectId = result.project_id ?? payloadProjectId ?? projectId;
      setLastUndoRedoProjectId(resultProjectId);
      await refreshUndoRedoAvailability(queryClient, resultProjectId);
    }
    return result;
  } finally {
    setPending('redo', false);
  }
}
